import * as React from 'react';
import {useSelector} from 'react-redux';

import {reduxForm, formValueSelector} from 'redux-form';
// @ts-expect-error
import type {FormProps} from 'redux-form/lib/types.js.flow';

import type {Assignable, Stageset, Source, Market, Competitor} from 'nutshell-core/types';

import {LeadIcon} from 'shells/icon/svg/lead-icon';

import type {GetFields_createModalFields as CreateModalField} from 'nutshell-graphql-types';

import type {FlashMessage} from '../../creator/types';

import {DefaultFooter} from '../default-footer/default-footer';
import {BulkCreateLeadFooter} from '../footers/bulk-create-lead-footer';
import {FormWrapper} from '../form-wrapper/form-wrapper';

import {getFieldsToDisplay, getFieldComponent} from './helpers';

import styles from '../form-common.module.css';

const ENTITY_FORM_TYPE = 'leads';

export type MassSelectOptions = {
    omittedItems: string[];
    isMassSelect: boolean;
    filters: any[];
    q: string | null | undefined;
    selectedCount: number;
};

// Form key
export const LEADS_FORM_KEY = 'leads_modal';

// State keys related to this form
export const NUM_LEADS_CREATING = 'numLeadsCreating';
export const RELATED_ENTITY = 'relatedEntity';
export const IS_MASS_SELECT = 'isMassSelect';
export const CREATOR_ORIGIN_ID = 'creatorOriginId';
export const MASS_SELECT_OPTIONS = 'massSelectOptions';

// Field rendered in BulkCreateLeadFooter
export const IS_CREATING_SINGLE_LEAD_FROM_MULTIPLE = 'isCreatingSingleLeadFromMultiple';

// Default values/options related to this form
export const RELATED_ENTITY_ACCOUNT_OPTION = {
    name: 'Company name',
    id: 'relatedEntityAccount',
    type: 'accounts',
} as const;
export const RELATED_ENTITY_CONTACT_OPTION = {
    name: 'Person name',
    id: 'relatedEntityContact',
    type: 'contacts',
} as const;

type Props = {
    schema: any;
    selectedFields: CreateModalField[];
    isLoading: boolean;
    handleSubmit: FormProps['handleSubmit'];
    reset: FormProps['reset'];
    submitting: FormProps['submitting'];
    submitFailed: FormProps['submitFailed'];
    submitSucceeded: FormProps['submitSucceeded'];
    onCancelClick: () => void;

    pushFlashMessage: (arg1: FlashMessage) => void;

    usersAndTeams: Assignable[];

    stagesets: Stageset[];

    sources: Source[];

    markets: Market[];

    competitors: Competitor[];
    currentMarket: Market | null | undefined;
    numSelectedContacts: number;
    numSelectedAccounts: number;
    bulkCreateFields: any;
    massSelectOptions: MassSelectOptions;
};

export function LeadFormConnectedComponent(props: Props) {
    function handleCancelClick() {
        props.onCancelClick();
        props.reset();
    }

    // Disable fields when form is submitting and after the form has been submitted successfully
    const isDisabled = props.submitSucceeded || props.submitting;

    const numLeadsCreating = props.bulkCreateFields[NUM_LEADS_CREATING];
    const relatedEntity = props.bulkCreateFields[RELATED_ENTITY];

    const isCreatingSingleLeadFromMultiple =
        props.bulkCreateFields[IS_CREATING_SINGLE_LEAD_FROM_MULTIPLE];
    const isBulkCreating = Boolean(numLeadsCreating && numLeadsCreating > 1 && relatedEntity);
    const nameFieldIsReadOnly = isBulkCreating && !isCreatingSingleLeadFromMultiple;
    const companyFieldIsReadOnly =
        (isBulkCreating && !isCreatingSingleLeadFromMultiple) ||
        (isBulkCreating && relatedEntity === 'contacts');
    const personFieldIsReadOnly =
        (isBulkCreating && !isCreatingSingleLeadFromMultiple) ||
        (isBulkCreating && relatedEntity === 'accounts');

    const selector = formValueSelector(LEADS_FORM_KEY);
    // @ts-expect-error
    const selectedContacts = useSelector((state) => selector(state, 'links.contacts'));
    // @ts-expect-error
    const selectedAccounts = useSelector((state) => selector(state, 'links.accounts'));

    const footerComponent = isBulkCreating ? (
        <BulkCreateLeadFooter
            onCancel={handleCancelClick}
            isSaving={props.submitting}
            errorSaving={props.submitFailed}
            handleSubmit={props.handleSubmit}
            relatedEntity={relatedEntity}
            isCreatingSingleLeadFromMultiple={isCreatingSingleLeadFromMultiple}
            numLeadsCreating={numLeadsCreating}
            isDisabled={isDisabled}
            creatorOriginId={props.bulkCreateFields[CREATOR_ORIGIN_ID]}
            numSelectedEntities={
                relatedEntity === 'contacts' ? props.numSelectedContacts : props.numSelectedAccounts
            }
        />
    ) : (
        <DefaultFooter
            onCancel={handleCancelClick}
            isSaving={props.submitting}
            errorSaving={props.submitFailed}
            buttonText='Create lead'
            savingText='Creating lead…'
        />
    );

    const fields = getFieldsToDisplay({
        schema: props.schema,
        selectedFields: props.selectedFields,
        entityType: ENTITY_FORM_TYPE,
        selectedContacts,
        selectedAccounts,
        nameFieldIsReadOnly,
        companyFieldIsReadOnly,
        personFieldIsReadOnly,
        relatedEntity,
    });

    const fieldComponents = fields.map((field) =>
        getFieldComponent({
            field,
            entityType: ENTITY_FORM_TYPE,
            currentMarket: props.currentMarket,
        })
    );

    return (
        <form onSubmit={props.handleSubmit} className={styles.form}>
            <FormWrapper
                icon={LeadIcon}
                // @ts-expect-error
                reset={props.reset}
                footer={footerComponent}
                isLoading={props.isLoading}
            >
                {fieldComponents}
            </FormWrapper>
        </form>
    );
}

export const LeadFormConnected = reduxForm({
    form: LEADS_FORM_KEY,
    // @ts-expect-error
})(LeadFormConnectedComponent);
